export class CampaignStatisticsBlock {
  private minAnimationDuration: number = 500;
  private maxAnimationDuration: number = 2000;
  private stepDuration: number = 8;

  constructor() {
    this.init();
  }

  init() {
    // Don't use countspinner if the user prefers reduced motion
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (prefersReducedMotion) return;

    const statisticsBlocks: NodeListOf<Element> = document.querySelectorAll(
      ".campaign-statistics-block",
    );
    statisticsBlocks.forEach((component) => {
      const figures = component.querySelectorAll(
        ".figure",
      ) as NodeListOf<HTMLElement>;

      figures.forEach((figure) => {
        this.attachObserver(figure);
      });
    });
  }

  attachObserver(figure: Element) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            observer.unobserve(entry.target);
            this.startCount(entry.target);
          }
        });
      },
      {
        threshold: 0.2,
      },
    );
    observer.observe(figure);
  }

  startCount(figure: Element) {
    const valueElement = figure.querySelector(".value") as HTMLElement;
    const rawValue = valueElement.dataset.counterValue
      ? valueElement.dataset.counterValue
      : null;
    const value = this.getValidNumber(rawValue);
    const decimalPlaces = this.getDecimalPlaces(
      valueElement.dataset.counterDecimals,
    );

    if (value === null) {
      console.log(`The value ${value} is not a number`);
      return;
    }

    const startTime = performance.now();
    const animationDuration = this.getAnimationDuration(value);

    const step = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / animationDuration, 1);
      const easedProgress = this.easeOutCubic(progress);
      const currentValue = this.roundToDecimalPlaces(
        value * easedProgress,
        decimalPlaces,
      );

      valueElement.innerText = this.formatValue(currentValue, decimalPlaces);

      if (progress < 1) {
        window.requestAnimationFrame(step);
        return;
      }

      valueElement.innerText = this.formatValue(value, decimalPlaces);
    };

    valueElement.innerText = this.formatValue(0, decimalPlaces);
    window.requestAnimationFrame(step);
  }

  getValidNumber(value: string | null) {
    if (value === null) {
      return null;
    }

    const normalizedValue = value.replace(",", ".");
    const parsedValue = Number.parseFloat(normalizedValue);

    return Number.isNaN(parsedValue) ? null : parsedValue;
  }

  getDecimalPlaces(value: string | undefined) {
    if (!value) {
      return 0;
    }

    const parsedValue = Number.parseInt(value, 10);
    return Number.isNaN(parsedValue) ? 0 : parsedValue;
  }

  formatValue(value: number, decimalPlaces: number) {
    return new Intl.NumberFormat(undefined, {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    }).format(value);
  }

  getAnimationDuration(value: number) {
    const uncappedDuration = Math.abs(value) * this.stepDuration;

    return Math.min(
      Math.max(uncappedDuration, this.minAnimationDuration),
      this.maxAnimationDuration,
    );
  }

  roundToDecimalPlaces(value: number, decimalPlaces: number) {
    if (decimalPlaces === 0) {
      return Math.round(value);
    }

    const multiplier = Math.pow(10, decimalPlaces);
    return Math.round(value * multiplier) / multiplier;
  }

  easeOutCubic(value: number) {
    return 1 - Math.pow(1 - value, 3);
  }
}
